import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { uploadDocumentAction } from "../../actions/uploadDocumentAction";
import { fetchRequiredDocumentsAction } from "../../actions/fetchRequiredDocumentsAction";
import { toast } from "react-toastify";
import { validateFile } from "../../utils/fileValidationUtils";

export const useUploadDocuments = (searchParams) => {
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [requiredDocumentsField, setRequiredDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  // Guards — prevent concurrent fetches and concurrent uploads per document
  const isFetchingRef = useRef(false);
  const uploadingKeysRef = useRef(new Set());

  const entityCategory = searchParams.get("entityCategory");
  const selectedlobtypenationality = searchParams.get(
    "selectedlobtypenationality",
  );
  const entityName = searchParams.get("entityName");
  const userId = searchParams.get("onboardingId");
  const entityId = searchParams.get("entityId");
  const actionTakenBy = searchParams.get("type") || "";

  // Stable ref so handlers can always call the latest version without
  // being listed as useEffect deps (avoids re-fetch loops)
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  const fetchRequiredDocuments = useCallback(async () => {
    const id = userIdRef.current;
    if (!id) return;

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      // console.log("[Docs] Fetch already in progress — skipped");
      return;
    }

    isFetchingRef.current = true;
    setIsLoadingDocuments(true);
    // console.log("[Docs] Fetching required documents for userId:", id);

    try {
      const result = await fetchRequiredDocumentsAction(id);
      if (result.success) {
        setRequiredDocuments(result.documents);
      } else {
        setErrors((prev) => ({
          ...prev,
          fetch: result.error || "Failed to load required documents",
        }));
      }
    } catch (error) {
      console.error("[Docs] Fetch error:", error);
      setErrors((prev) => ({
        ...prev,
        fetch: "Failed to load required documents. Please refresh the page.",
      }));
    } finally {
      isFetchingRef.current = false;
      setIsLoadingDocuments(false);
    }
  }, []); // no deps — uses ref internally, so this never re-creates

  // Single mount fetch — runs once when userId becomes available
  useEffect(() => {
    if (userId) fetchRequiredDocuments();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isValidParams = useMemo(() => {
    return (
      entityCategory &&
      (entityCategory === "client" || entityCategory === "vendor") &&
      selectedlobtypenationality &&
      (selectedlobtypenationality === "Domestic" ||
        selectedlobtypenationality === "International" ||
        selectedlobtypenationality === "international")
    );
  }, [entityCategory, selectedlobtypenationality]);

  const pageTitle = useMemo(() => {
    if (!isValidParams) return "";
    const nationality =
      selectedlobtypenationality.charAt(0).toUpperCase() +
      selectedlobtypenationality.slice(1).toLowerCase();
    const category =
      entityCategory === "client" ? "Client" : "Service Provider";
    return `${nationality} ${category} - Upload Documents`;
  }, [entityCategory, selectedlobtypenationality, isValidParams]);

  const handleRemoveFile = (documentId, fileIndex) => {
    const updatedFiles = (uploadedFiles[documentId] || []).filter(
      (_, i) => i !== fileIndex,
    );
    if (updatedFiles.length === 0) {
      const nf = { ...uploadedFiles };
      delete nf[documentId];
      setUploadedFiles(nf);
      const ns = { ...uploadingStatus };
      delete ns[documentId];
      setUploadingStatus(ns);
    } else {
      setUploadedFiles({ ...uploadedFiles, [documentId]: updatedFiles });
    }
  };

  // ── New upload ────────────────────────────────────────────────────────────
  const handleFileSelect = async (e, document) => {
    const files = Array.from(e.target.files);
    if (!files.length || !document) return;

    const documentKey =
      document.entityDocumentId && document.entityDocumentId !== 0
        ? document.entityDocumentId
        : document.documentRequirementId;
    const documentRequirementId = document.documentRequirementId;
    const user_id = document.entityId || entityId;
    const entityDocumentId = document.entityDocumentId || 0;
    const file = files[0];

    // Guard — prevent double upload for same document
    if (uploadingKeysRef.current.has(documentKey)) {
      console.warn(
        "[Upload] Already uploading for key:",
        documentKey,
        "— skipped",
      );
      return;
    }

    const validation = validateFile(file);
    if (!validation.isValid) {
      console.warn("[Upload] Validation failed:", validation.error);
      setErrors((prev) => ({ ...prev, [documentKey]: validation.error }));
      return;
    }
    // console.log("[Upload] Validation passed");

    uploadingKeysRef.current.add(documentKey);
    setUploadingStatus((prev) => ({ ...prev, [documentKey]: "uploading" }));
    setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));

    try {
      const uploadResult = await uploadDocumentAction({
        file,
        documentRequirementId,
        entityId: user_id,
        entityDocumentId,
        actionType: "upload",
        actionTakenBy,
        onProgress: (progress) => {
          setUploadProgress((prev) => ({ ...prev, [documentKey]: progress }));
        },
      });

      // console.log("[Upload] Result:", uploadResult);

      if (uploadResult.success) {
        setUploadedFiles((prev) => ({ ...prev, [documentKey]: [file] }));
        setUploadingStatus((prev) => ({ ...prev, [documentKey]: "success" }));
        setUploadProgress((prev) => ({ ...prev, [documentKey]: 100 }));

        await fetchRequiredDocuments();
      } else {
        console.error("[Upload] Failed:", uploadResult.error);
        setErrors((prev) => ({
          ...prev,
          [documentKey]: uploadResult.error || "Upload failed",
        }));
        setUploadingStatus((prev) => ({ ...prev, [documentKey]: "error" }));
        setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));
        toast.error(uploadResult.error?.message || "Upload failed");
      }
    } catch (error) {
      console.error("[Upload] Exception:", error);
      setErrors((prev) => ({
        ...prev,
        [documentKey]: "Failed to upload. Please try again.",
      }));
      setUploadingStatus((prev) => ({ ...prev, [documentKey]: "error" }));
      setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));
    } finally {
      uploadingKeysRef.current.delete(documentKey);
    }
  };

  // ── Re-upload ─────────────────────────────────────────────────────────────
  const handleReupload = async (e, file, document) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const newFile = files[0];
    const documentKey = file.entityDocumentId;
    const user_id = document.entityId || entityId;

    // Guard — prevent double reupload for same file
    if (uploadingKeysRef.current.has(documentKey)) {
      console.warn(
        "[Reupload] Already uploading for key:",
        documentKey,
        "— skipped",
      );
      return;
    }

    const validation = validateFile(newFile);
    if (!validation.isValid) {
      console.warn("[Reupload] Validation failed:", validation.error);
      setErrors((prev) => ({ ...prev, [documentKey]: validation.error }));
      return;
    }
    // console.log("[Reupload] Validation passed");

    uploadingKeysRef.current.add(documentKey);
    setUploadingStatus((prev) => ({ ...prev, [documentKey]: "uploading" }));
    setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));

    try {
      const result = await uploadDocumentAction({
        file: newFile,
        documentRequirementId: document.documentRequirementId,
        entityId: user_id,
        entityDocumentId: file.entityDocumentId,
        actionType: "reupload",
        actionTakenBy,
        onProgress: (progress) => {
          setUploadProgress((prev) => ({ ...prev, [documentKey]: progress }));
        },
      });

      // console.log("[Reupload] Result:", result);

      if (result.success) {
        setUploadingStatus((prev) => ({ ...prev, [documentKey]: "success" }));
        setUploadProgress((prev) => ({ ...prev, [documentKey]: 100 }));

        await fetchRequiredDocuments();
      } else {
        console.error("[Reupload] Failed:", result.error);
        setErrors((prev) => ({
          ...prev,
          [documentKey]: result.error || "Re-upload failed",
        }));
        setUploadingStatus((prev) => ({ ...prev, [documentKey]: "error" }));
        setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));
        toast.error("Re-upload failed");
      }
    } catch (error) {
      console.error("[Reupload] Exception:", error);
      setErrors((prev) => ({
        ...prev,
        [documentKey]: "Failed to re-upload. Please try again.",
      }));
      setUploadingStatus((prev) => ({ ...prev, [documentKey]: "error" }));
      setUploadProgress((prev) => ({ ...prev, [documentKey]: 0 }));
    } finally {
      uploadingKeysRef.current.delete(documentKey);
    }
  };

  return {
    uploadedFiles,
    errors,
    pageTitle,
    entityName,
    entityId,
    isValidParams,
    uploadingStatus,
    uploadProgress,
    requiredDocumentsField,
    isLoadingDocuments,
    handleRemoveFile,
    handleFileSelect,
    handleReupload,
  };
};
